import L from 'leaflet';
import 'proj4leaflet';
import geojson from 'geojson';

export enum ProjectionCode {
  Arctic = 'EPSG3995',
  Mercator = 'EPSG3857',
  Antarctic = 'EPSG3031',
}

export function getProjectionCode(feature: geojson.Feature): ProjectionCode {
  const code = feature.properties?.['projection'];
  switch (code) {
    case 'EPSG3575':
    case ProjectionCode.Arctic:
      return ProjectionCode.Arctic;
    case ProjectionCode.Antarctic:
      return ProjectionCode.Antarctic;
    default:
      return ProjectionCode.Mercator;
  }
}

export interface Projection {
  code: ProjectionCode;

  minZoom: number;
  maxZoom: number;
  defaultZoom: number;

  center: L.LatLngExpression;
  viewBounds?: L.LatLngBoundsExpression;

  tilesets: {
    source: string;
    size: number;
  }[]

  crs: L.CRS;
}

export const ProjectionArctic = {
  code: ProjectionCode.Arctic,

  minZoom: 1,
  maxZoom: 10,
  defaultZoom: 2,

  center: [90, 0] as L.LatLngExpression,
  viewBounds: L.latLngBounds(
    L.latLng(35, -35), // sw corner
    L.latLng(35, 145), // ne corner
  ),

  tilesets: [
    {
      source: "https://geodata.npolar.no/arcgis/rest/services/Basisdata_Intern/NP_Arktis_WMTS_3995/MapServer/tile/{z}/{y}/{x}",
      size: 256,
    }
  ],

  crs: new L.Proj.CRS(
    "EPSG:3995",
    "+proj=stere +lat_0=90 +lat_ts=71 +lon_0=0 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs +type=crs",
    {
      origin: [-30636100, 30636100],
      bounds: L.bounds([
        [-4524537.7065452775, -4524806.246410551],
        [4524742.293468646, 4527033.753589449],
      ]),
      resolutions: [
        21674.7100160867,
        10837.35500804335,
        5418.677504021675,
        2709.3387520108377,
        1354.6693760054188,
        677.3346880027094,
        338.6673440013547,
        169.33367200067735,
        84.66683600033868,
        42.33341800016934,
        21.16670900008467,
        10.583354500042335,
        5.291677250021167,
        2.6458386250105836,
      ],
    })
} as Projection;

export const ProjectionAntarctic = {
  code: ProjectionCode.Antarctic,

  minZoom: 1,
  maxZoom: 10,
  defaultZoom: 2,

  center: [-90, 0] as L.LatLngExpression,
  viewBounds: L.latLngBounds(
    L.latLng(-35, -135), // sw corner
    L.latLng(-35, 45), // ne corner
  ),

  tilesets: [
    {
      source: "https://geodata.npolar.no/arcgis/rest/services/Basisdata_Intern/NP_Antarktis_WMTS_3031/MapServer/tile/{z}/{y}/{x}",
      size: 256,
    },
  ],

  crs: new L.Proj.CRS(
    "EPSG:3031",
    "+proj=stere +lat_0=-90 +lat_ts=-71 +lon_0=0 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs +type=crs",
    {
      origin: [-30636100, 30636100],
      bounds: L.bounds([
        [-4525955.44727179, -4526044.552728202],
        [4526044.55272821, 4525955.447271798],
      ]),
      resolutions: [
        21674.7100160867,
        10837.35500804335,
        5418.677504021675,
        2709.3387520108377,
        1354.6693760054188,
        677.3346880027094,
        338.6673440013547,
        169.33367200067735,
        84.66683600033868,
        42.33341800016934,
        21.16670900008467,
        10.583354500042335,
        5.291677250021167,
        2.6458386250105836,
      ]
    },
  ),
} as Projection;

export const ProjectionMercator = {
  code: ProjectionCode.Mercator,

  minZoom: 2,
  maxZoom: 15,
  defaultZoom: 4,

  center: [0, 0] as L.LatLngExpression,
  viewBounds: L.latLngBounds(
    L.latLng(-85, -180), // sw corner
    L.latLng(85, 180), // ne corner
  ),

  tilesets: [
    {
      source: "https://services.arcgisonline.com/arcgis/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
      size: 256,
    },
  ],

  crs: new L.Proj.CRS(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs",
    {
      origin: [-2.0037508342787E7, 2.0037508342787E7],
      bounds: L.bounds([
        [-2.0037507067161843E7, -1.999070943504232E7],
        [2.0037507067161843E7, 2.0034863616507012E7],
      ]),
      resolutions: [
        156543.03392800014,
        78271.51696399994,
        39135.75848200009,
        19567.87924099992,
        9783.93962049996,
        4891.96981024998,
        2445.98490512499,
        1222.992452562495,
        611.4962262813797,
        305.74811314055756,
        152.87405657041106,
        76.43702828507324,
        38.21851414253662,
        19.10925707126831,
        9.554628535634155,
        4.77731426794937,
        2.388657133974685,
        1.1943285668550503,
        0.5971642835598172,
        0.29858214164761665,
        0.14929107082380833,
        0.07464553541190416,
        0.03732276770595208,
        0.01866138385297604,
      ]
    },
  ),
} as Projection;
